// src/App.tsx
import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, Outlet } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

// Components
import { Header } from '@/components/layout/Header';
// import { NotFound } from '@/pages/NotFound';
import { PermissionWrapper } from '@/components/PermissionWrapper';
// import SomeComponent from '@/pages/SomeComponent';
// import { PageListMain, PageFormMain } from '@/components/page-content-v4';

// Contexts and Providers
import { PageProvider } from '@/contexts/PageContext';
import { AuthProvider } from '@/contexts/AuthContext';
import PrivateRoutes from '@/contexts/PrivateRoutes';
// import { WebSocketProvider } from '@/contexts/WebSocketContext';

// // Types
// import { TemplateType } from '@/components/template-builder-v2/types';

// // Hooks
// import { useGlobalPermission } from '@/hooks/useGlobalPermission';

// // Utils
import { PERMISSIONS } from '@/utils/roles';
import NetworkErrorPage from './pages/NetworkErrorPage';

// Lazy-loaded components
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const Settings = lazy(() => import('@/pages/Settings'));
const ProfilePage = lazy(() => import('@/pages/ProfilePage'));
const AuthPage = lazy(() => import('@/pages/Auth'));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'));
const TicketView = lazy(() => import('@/pages/TicketView'));
const UserList = lazy(() => import('@/components/users/UserList'));
const UserDetail = lazy(() => import('@/components/users/UserDetail'));
const EmailManagement = lazy(() => import('@/components/email-template-management-v1'));
const Contact = lazy(() => import('@/pages/Contact'));

// Support V4
const SupportV4 = lazy(() => import('@/components/support-v4/Support'));

const App: React.FC = () => {
  return (
    <Router>
      <Toaster position="top-center" reverseOrder={false} />
      <AppRoutes />
    </Router>
  );
};

const AppRoutes: React.FC = () => {
  return (
    <AuthProvider>
      <Routes>
        {/* Auth route with minimal layout */}
        <Route path="/auth" element={
          <Suspense fallback={<div>Loading auth...</div>}>
            <AuthPage />
          </Suspense>
        } />
        <Route path="/forgot-password" element={
          <Suspense fallback={<div>Loading forgot password...</div>}>
            <ForgotPassword />
          </Suspense>
        } />
        <Route path="/network-error" element={
          <Suspense fallback={<div>Loading network error...</div>}>
            <NetworkErrorPage />
          </Suspense>
        } />

        {/* All other routes with full layout */}
        <Route element={<PrivateRoutes />}>
          <Route element={
            <PageProvider>
                  <Header />
                  <Suspense fallback={<div className='flex items-center justify-center w-full h-screen'>Loading...</div>}>
                    <Outlet />
                  </Suspense>
            </PageProvider>
          }>
            <Route path="/" element={
              <Suspense fallback={<div>Loading dashboard...</div>}>
                <Dashboard />
              </Suspense>
            } />
            <Route path="/users" element={
              <Suspense fallback={<div>Loading users...</div>}>
                <UserList />
              </Suspense>
            } />
            <Route path="/users/:userId" element={
              <Suspense fallback={<div>Loading user detail...</div>}>
                <UserDetail />
              </Suspense>
            } />
            {/* <Route path="/subscriptions" element={
              <Suspense fallback={<div>Loading subscriptions...</div>}>
                <Subscriptions />
              </Suspense>
            } /> */}
            <Route path="/profile" element={
              <Suspense fallback={<div>Loading profile...</div>}>
                <ProfilePage />
              </Suspense>
            } />
            <Route path="/settings" element={<Navigate to="/settings/team" replace />} />
            <Route path="/settings/:tab" element={
              <Suspense fallback={<div>Loading settings...</div>}>
                <Settings />
              </Suspense>
            } />
            <Route path="/support/ticket/:id" element={
              <Suspense fallback={<div>Loading ticket...</div>}>
                <PermissionWrapper permission={PERMISSIONS.GENERIC_PERMISSION}>
                  <TicketView />
                </PermissionWrapper>
              </Suspense>
            } />
            {/* Support V3 */}
            <Route path="/support" element={
              <Suspense fallback={<div>Loading support v3...</div>}>
                <SupportV4 />
              </Suspense>
            } />
            <Route path="/email-template-management" element={
              <Suspense fallback={<div>Loading email template management...</div>}>
                <EmailManagement mode="list" />
              </Suspense>
            } />
             <Route path="/email-template-management/create" element={
              <Suspense fallback={<div>Loading email template management...</div>}>
                <EmailManagement mode="create" />
              </Suspense>
            } />
            <Route path="/email-template-management/edit/:id" element={
              <Suspense fallback={<div>Loading email template management...</div>}>
                <EmailManagement mode="edit" />
              </Suspense>
            } />
            <Route path="/contact" element={
              <Suspense fallback={<div>Loading support v3...</div>}>
                <Contact />
              </Suspense>
            } />
          </Route>
        </Route>
      </Routes>
    </AuthProvider>
  );
};

export default App;
